<template>
  <div class="bg-white border border-dashboard-border rounded-xl shadow-sm p-4 mb-6">
    <div class="flex flex-col lg:flex-row lg:items-end gap-3">
      <!-- Période -->
      <div class="flex-1">
        <label class="block text-xs font-medium text-dashboard-muted mb-1">Période</label>
        <div class="flex items-center gap-2 mb-2">
          <button
            @click="periodMode = 'range'"
            :class="['px-3 py-1 rounded-md text-xs font-medium border transition-colors',
                     periodMode === 'range' ? 'bg-[#22282F] text-white border-[#22282F]' : 'bg-white text-dashboard-text border-dashboard-border']"
          >Intervalle</button>
          <button
            @click="periodMode = 'date'"
            :class="['px-3 py-1 rounded-md text-xs font-medium border transition-colors',
                     periodMode === 'date' ? 'bg-[#22282F] text-white border-[#22282F]' : 'bg-white text-dashboard-text border-dashboard-border']"
          >Date fixe</button>
        </div>
        <div v-if="periodMode === 'range'" class="flex items-center gap-2">
          <input v-model="from" type="date" class="px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm" />
          <span class="text-dashboard-muted text-sm">→</span>
          <input v-model="to" type="date" class="px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm" />
        </div>
        <div v-else>
          <input v-model="date" type="date" class="px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm" />
        </div>
      </div>

      <!-- Sélecteurs de périmètre -->
      <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2 flex-[2]">
        <div>
          <label class="block text-xs font-medium text-dashboard-muted mb-1">Ambassade(s)</label>
          <MultiSelectDropdown
            label="Toutes"
            :options="ambassadeOptions"
            v-model="ambassade"
            :mode="ambassadeMode"
            @update:mode="ambassadeMode = $event"
            show-mode
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-dashboard-muted mb-1">Direction(s)</label>
          <MultiSelectDropdown
            label="Toutes"
            :options="directionOptions"
            v-model="direction"
            :mode="directionMode"
            @update:mode="directionMode = $event"
            show-mode
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-dashboard-muted mb-1">Rôle(s) — détenteur</label>
          <MultiSelectDropdown
            label="Tous"
            :options="roleOptions"
            v-model="role"
            :mode="roleMode"
            @update:mode="roleMode = $event"
            show-mode
          />
          <button
            type="button"
            @click="excludeCabinet"
            class="mt-1 text-[11px] text-red-600 hover:underline"
            title="Exclure Ministre, DC, SG, SGA, Assistant SGA, SP…"
          >Exclure le cabinet</button>
        </div>
        <div>
          <label class="block text-xs font-medium text-dashboard-muted mb-1">Agent</label>
          <select v-model="user" class="w-full px-2 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm">
            <option value="">Tous</option>
            <option v-for="u in users" :key="u.id" :value="String(u.id)">{{ userLabel(u) }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-dashboard-muted mb-1">Étape</label>
          <select v-model="stage" class="w-full px-2 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm">
            <option value="">Toutes</option>
            <option v-for="s in STAGES" :key="s.value" :value="s.value">{{ s.label }}</option>
          </select>
        </div>
      </div>

      <!-- Reset -->
      <div>
        <button
          v-if="hasActiveFilters"
          @click="reset"
          class="px-3 py-2 text-dashboard-muted hover:text-dashboard-text text-sm flex items-center gap-1 border border-dashed border-dashboard-border rounded-lg hover:bg-dashboard-bg"
        >
          <X class="w-4 h-4" /> Réinitialiser
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { X } from 'lucide-vue-next'
import { apiFetch } from '@/utils/apiFetch'
import MultiSelectDropdown from './MultiSelectDropdown.vue'

const props = withDefaults(defineProps<{
  ambassadeDefaultMode?: string
  directionDefaultMode?: string
}>(), { ambassadeDefaultMode: 'include', directionDefaultMode: 'exclude' })

const emit = defineEmits<{ (e: 'change', params: Record<string, string>): void }>()

const STAGES = [
  { value: 'en_attente', label: 'En attente' },
  { value: 'envoye_sg', label: 'Envoyé au SG' },
  { value: 'chez_ambassadeur', label: "Chez l'ambassadeur" },
  { value: 'affecte', label: 'Affecté' },
  { value: 'en_traitement', label: 'En traitement' },
  { value: 'valide', label: 'Validé' },
  { value: 'en_attente_archivage', label: "En attente d'archivage" },
  { value: 'archive', label: 'Archivé' },
]

// Par défaut : 30 derniers jours
const today = new Date()
const monthAgo = new Date(today.getTime() - 30 * 24 * 3600 * 1000)
const iso = (d: Date) => d.toISOString().slice(0, 10)

const periodMode = ref<'range' | 'date'>('range')
const from = ref(iso(monthAgo))
const to = ref(iso(today))
const date = ref(iso(today))
const ambassade = ref<string[]>([])
const direction = ref<string[]>([])
const ambassadeMode = ref(props.ambassadeDefaultMode)
const directionMode = ref(props.directionDefaultMode)
const role = ref<string[]>([])
const roleMode = ref('include')
const user = ref('')
const stage = ref('')

// Rôles « cabinet » (haut de la hiérarchie) qui polluent le global.
const CABINET_ROLES = ['MINISTRE', 'DC', 'SG', 'SGA', 'ASSISTANT_SGA', 'SP_MINISTRE', 'SP_DC', 'SP_SG']

const ambassades = ref<any[]>([])
const directions = ref<any[]>([])
const roles = ref<any[]>([])
const users = ref<any[]>([])

const ambassadeOptions = computed(() =>
  ambassades.value.map((a) => ({ value: String(a.id), label: a.nom })))
const directionOptions = computed(() =>
  directions.value.map((d) => ({ value: String(d.id), label: d.nom })))
const roleOptions = computed(() =>
  roles.value.map((r) => ({ value: String(r.code), label: r.nom || r.code })))

const userLabel = (u: any) =>
  (`${u.first_name || ''} ${u.last_name || ''}`.trim()) || u.full_name || u.username || `#${u.id}`

function excludeCabinet() {
  const codes = new Set(roles.value.map((r) => String(r.code)))
  role.value = CABINET_ROLES.filter((c) => codes.size === 0 || codes.has(c))
  roleMode.value = 'exclude'
}

const hasActiveFilters = computed(() =>
  Boolean(ambassade.value.length || direction.value.length || role.value.length || user.value || stage.value),
)

function buildParams(): Record<string, string> {
  const p: Record<string, string> = {}
  if (periodMode.value === 'date') {
    if (date.value) p.date = date.value
  } else {
    if (from.value) p.from = from.value
    if (to.value) p.to = to.value
  }
  if (ambassade.value.length) {
    p.ambassade = ambassade.value.join(',')
    p.ambassade_mode = ambassadeMode.value
  }
  if (direction.value.length) {
    p.direction = direction.value.join(',')
    p.direction_mode = directionMode.value
  }
  if (role.value.length) {
    p.role = role.value.join(',')
    p.role_mode = roleMode.value
  }
  if (user.value) p.user = user.value
  if (stage.value) p.stage = stage.value
  return p
}

let debounce: ReturnType<typeof setTimeout> | undefined
function emitChange() {
  if (debounce) clearTimeout(debounce)
  debounce = setTimeout(() => emit('change', buildParams()), 250)
}

watch(
  [periodMode, from, to, date, ambassade, direction, ambassadeMode, directionMode,
   role, roleMode, user, stage],
  emitChange,
  { deep: true },
)

function reset() {
  ambassade.value = []
  direction.value = []
  ambassadeMode.value = props.ambassadeDefaultMode
  directionMode.value = props.directionDefaultMode
  role.value = []
  roleMode.value = 'include'
  user.value = ''
  stage.value = ''
}

async function loadOptions() {
  const safe = async (path: string) => {
    try {
      const res = await apiFetch(path)
      return Array.isArray(res) ? res : (res?.data || [])
    } catch {
      return []
    }
  }
  ;[directions.value, ambassades.value, roles.value, users.value] = await Promise.all([
    safe('/api/admin/directions/'),
    safe('/api/admin/ambassades/'),
    safe('/api/admin/roles/'),
    safe('/api/admin/users/'),
  ])
}

onMounted(() => {
  loadOptions()
  emit('change', buildParams())
})
</script>
